import { CheckCircle2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { useAuth } from '@/features/auth/hooks/useAuth'
import { Badge } from '@/shared/components/ui/badge'
import { Progress } from '@/shared/components/ui/progress'
import {
  getDashboardPathForRole,
  getRoleShortLabel,
} from '@/shared/constants/roles'
import { ROUTES } from '@/shared/constants/routes'
import { toast } from '@/shared/lib/toast'

const WELCOME_LOAD_MS = 3200

export function WelcomeLoaderPage() {
  const navigate = useNavigate()
  const { user, role, isAuthenticated } = useAuth()
  const [progress, setProgress] = useState(8)

  const roleLabel = getRoleShortLabel(role)
  const displayName = useMemo(() => {
    return user?.name?.trim() || 'there'
  }, [user?.name])

  useEffect(() => {
    if (!isAuthenticated || !role) return

    if (!hasShownToast.current) {
      hasShownToast.current = true
      toast.success(`Welcome · ${roleLabel.toUpperCase()} workspace`)
    }
    const start = Date.now()
    const tick = window.setInterval(() => {
      const elapsed = Date.now() - start
      setProgress(Math.min(98, Math.round((elapsed / WELCOME_LOAD_MS) * 100)))
    }, 40)

    const done = window.setTimeout(() => {
      setProgress(100)
      navigate(getDashboardPathForRole(role), { replace: true })
    }, WELCOME_LOAD_MS)

    return () => {
      window.clearInterval(tick)
      window.clearTimeout(done)
    }
  }, [isAuthenticated, navigate, role, roleLabel])

  const hasShownToast = useRef(false)

  if (!isAuthenticated || !role) {
    return <Navigate to={ROUTES.LOGIN} replace />
  }

  return (
    <div
      className="relative grid min-h-dvh place-items-center overflow-hidden px-4 py-8 sm:px-6 sm:py-10"
      style={{
        background: `
          radial-gradient(circle at center, rgba(34, 211, 238, 0.08) 0%, transparent 42%),
          radial-gradient(circle at 50% 40%, rgba(59, 130, 246, 0.12) 0%, transparent 50%),
          #05070d
        `,
      }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{
          backgroundImage: `
            repeating-radial-gradient(
              circle at center,
              transparent 0,
              transparent 48px,
              rgba(148, 163, 184, 0.07) 49px,
              transparent 50px
            )
          `,
        }}
      />

      <div className="relative z-10 flex w-full max-w-lg flex-col items-center gap-4 text-center sm:gap-6">
        <div className="relative flex items-center justify-center">
          {/* Outer animated ring */}
          <div className="absolute h-20 w-20 animate-ping rounded-full border-2 border-cyan-400/40" />

          {/* Middle glow */}
          <div className="absolute h-16 w-16 rounded-full bg-cyan-400/10 blur-md" />

          {/* Logo */}
          <img
            src="/logo.jpeg"
            alt="Superior CRM"
            className="relative z-10 h-16 w-16 rounded-full bg-white object-contain p-1 shadow-[0_0_40px_rgba(34,211,238,0.25)]"
          />
        </div>

        <p className="text-[0.65rem] font-semibold tracking-[0.18em] text-sky-400 uppercase sm:text-[0.72rem] sm:tracking-[0.22em]">
          Welcome to Superior CRM
        </p>

        <h1 className="max-w-full px-2 text-[clamp(1.75rem,8vw,3.2rem)] font-bold leading-tight tracking-[-0.04em]">
          <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-teal-300 bg-clip-text text-transparent">
            {displayName}
          </span>
        </h1>

        <Badge className="max-w-full flex-wrap justify-center gap-2 border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-medium text-slate-200 sm:px-4 sm:text-sm">
          <CheckCircle2 className="size-4 shrink-0 text-green-500" />
          <span>
            You are logged in as{' '}
            <span className="font-semibold text-sky-400">{roleLabel}</span>
          </span>
        </Badge>

        <p className="max-w-md text-sm leading-relaxed text-slate-300/85 sm:text-[0.95rem]">
          Your {roleLabel} dashboard is loading with live CRM intelligence.
        </p>

        <div className="w-full max-w-sm pt-4">
          <Progress value={progress} />
          <div className="mt-3 flex items-center justify-center gap-2">
            <CheckCircle2 className="size-3.5 text-green-500" />
            <p className="text-xs font-medium tracking-wide text-slate-400">
              taking you to your dashboard...
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WelcomeLoaderPage
